import type { TicketConfig } from '@bifeai/shared';
import { Badge, Button, Callout, Card, CardBody, CardHeader, Field, Input, Select, Switch } from '@bifeai/ui';
import { Archive, Cable, Hand, Plug, Printer, Scale, Unplug, Wand2 } from 'lucide-react';
import { useEffect, useState, type ReactNode } from 'react';
import {
  DEFAULT_SCALE_LINK,
  SCALE_REQUEST_LABELS,
  setHardware,
  touchDetected,
  useHardware,
  useTouchMode,
  type ScaleLinkConfig,
  type ScaleRequest,
} from '../../lib/hardware';
import { connectScale, detectScale, disconnectScale, scaleFresh, scaleSupported, useScale, type DetectStep } from '../../lib/scale-link';
import { useSession } from '../../lib/session';
import { printDrawerSlip } from '../../lib/ticket-print';
import { formatQty } from '../../lib/units';

function Steps({ children }: { children: ReactNode }) {
  return <ol className="bf-hw-steps">{children}</ol>;
}

/**
 * Conexiones de ESTA computadora: balanza por cable y cajón de dinero.
 * Se guarda en la computadora (cada caja tiene sus aparatos), por eso no lleva "Guardar cambios".
 */
export function DevicesTab({ ticket }: { ticket: TicketConfig }) {
  return (
    <div className="bf-form" style={{ gap: 16 }}>
      <Callout>
        Esto se configura <strong>en cada computadora del mostrador</strong> y queda guardado en esa computadora. Hacelo desde la caja que tiene los
        aparatos enchufados.
      </Callout>
      <TouchCard />
      <ScaleLinkCard />
      <DrawerCard ticket={ticket} />
    </div>
  );
}

/* ---------------------------------------------------------------- pantalla táctil */
function TouchCard() {
  const hw = useHardware();
  const on = useTouchMode();
  const detected = touchDetected();
  const [demo, setDemo] = useState('');
  return (
    <Card>
      <CardHeader
        title={
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
            <Hand size={18} aria-hidden /> Pantalla táctil
          </span>
        }
        subtitle="Para computadoras “todo en uno” con pantalla táctil: se vende tocando, sin teclado ni mouse."
      />
      <CardBody>
        <div className="bf-form">
          <p className="bf-guide-body" style={{ margin: 0 }}>
            Con la pantalla táctil activada, cada vez que hay que escribir un número (peso, importe, con cuánto paga, descuento, fondo de caja)
            aparece un <strong>teclado numérico grande</strong> debajo del campo, en la misma ventana. Así no sale el teclado chico de Windows que
            tapa la pantalla. Los botones de la venta también se agrandan para el dedo.
          </p>
          <Field
            label="Usar modo táctil en esta computadora"
            hint={detected ? 'Se detectó una pantalla táctil.' : 'No se detectó pantalla táctil (podés activarlo igual).'}
          >
            <Select value={hw.touch} onChange={(e) => setHardware({ ...hw, touch: e.target.value as typeof hw.touch })}>
              <option value="auto">Automático (si la pantalla es táctil)</option>
              <option value="on">Siempre activado</option>
              <option value="off">Desactivado</option>
            </Select>
          </Field>
          {on && (
            <form className="bf-form" onSubmit={(e) => e.preventDefault()} style={{ maxWidth: 320 }}>
              <Field label="Probalo acá: tocá el campo">
                <Input inputMode="decimal" value={demo} onChange={(e) => setDemo(e.target.value)} placeholder="0,000" />
              </Field>
            </form>
          )}
        </div>
      </CardBody>
    </Card>
  );
}

/* ---------------------------------------------------------------- balanza conectada */
function ScaleLinkCard() {
  const hw = useHardware();
  const c = hw.scale;
  const scale = useScale();
  const [step, setStep] = useState<DetectStep | null>(null);
  const [result, setResult] = useState<string | null>(null);
  const [, tick] = useState(0);
  useEffect(() => {
    const t = setInterval(() => tick((n) => n + 1), 500);
    return () => clearInterval(t);
  }, []);
  useEffect(() => {
    if (c.enabled) void connectScale();
  }, [c.enabled]);

  const live = scaleFresh(scale);
  const setScale = (next: ScaleLinkConfig) => {
    setHardware({ ...hw, scale: next });
  };
  const change = async (patch: Partial<ScaleLinkConfig>) => {
    await disconnectScale();
    setScale({ ...c, ...patch });
    await connectScale();
  };

  async function detect() {
    setResult(null);
    const r = await detectScale(setStep);
    setStep(null);
    setResult(
      r
        ? `¡Listo! La balanza responde (${r.config.baudRate} baudios${r.config.request === 'none' ? '' : ', pidiendo el peso'}).`
        : 'No se encontró la balanza en ese puerto. Revisá que esté prendida, que el cable sea el correcto y que la salida de datos esté activada en su menú.',
    );
  }

  return (
    <Card>
      <CardHeader
        title={
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
            <Scale size={18} aria-hidden /> Balanza conectada a la computadora
          </span>
        }
        subtitle="Para cobrar pesando en la caja, sin etiquetas: tocás el producto y el peso aparece solo."
      />
      <CardBody>
        <div className="bf-form">
          {!scaleSupported && (
            <Callout tone="warning">
              Este navegador no puede leer balanzas. Usá <strong>Google Chrome</strong> o <strong>Microsoft Edge</strong> en una computadora (no
              funciona en celulares ni tablets).
            </Callout>
          )}
          <div className="bf-switch-row">
            <div>
              <strong>Usar balanza conectada en esta computadora</strong>
              <small>Si usás balanza con etiquetas y lector, dejalo apagado: eso se configura en la pestaña Balanza.</small>
            </div>
            <Switch
              checked={c.enabled}
              disabled={!scaleSupported}
              onCheckedChange={(v) => {
                if (!v) void disconnectScale();
                setScale({ ...c, enabled: v });
              }}
              label="Usar balanza conectada"
            />
          </div>

          {c.enabled && (
            <>
              <h4 className="bf-hw-title">Cómo se conecta</h4>
              <Steps>
                <li>
                  Fijate qué salida tiene la balanza atrás: <strong>USB</strong> o <strong>RS-232</strong> (un enchufe de 9 patitas, a veces dice “PC”
                  o “Serie”).
                </li>
                <li>
                  Si es RS-232, comprá un <strong>adaptador RS-232 a USB</strong> (se consigue en casas de computación). Si es USB, va directo.
                </li>
                <li>Enchufá la balanza a la computadora y prendela. Si Windows pide instalar el adaptador, dejá que lo haga.</li>
                <li>
                  Tocá <strong>Detectar automáticamente</strong>. Chrome muestra una ventanita: elegí el puerto de la balanza (suele decir “USB
                  Serial”, “CH340”, “Prolific” o “COM3”) y tocá <strong>Conectar</strong>.
                </li>
                <li>Poné algo sobre la balanza: el peso tiene que aparecer abajo. La próxima vez se conecta sola.</li>
              </Steps>
              <p className="bf-cell-sub">
                La balanza tiene que ser comercial y estar <strong>homologada</strong>. Algunas traen la salida de datos apagada: buscá en su menú
                “comunicación”, “RS-232” o “PC” y activala (el manual lo explica).
              </p>

              <div className="bf-form-actions" style={{ justifyContent: 'flex-start', flexWrap: 'wrap' }}>
                <Button variant="primary" icon={<Wand2 />} loading={!!step} onClick={() => void detect()}>
                  Detectar automáticamente
                </Button>
                <Button icon={<Plug />} disabled={!!step} onClick={() => void connectScale(true)}>
                  Elegir puerto
                </Button>
                {scale.status === 'connected' && (
                  <Button variant="ghost" icon={<Unplug />} disabled={!!step} onClick={() => void disconnectScale()}>
                    Desconectar
                  </Button>
                )}
              </div>
              {step && (
                <Callout>
                  Probando {step.index} de {step.total}: {step.label}…
                </Callout>
              )}
              {result && <Callout tone={result.startsWith('¡Listo') ? 'success' : 'warning'}>{result}</Callout>}
              {scale.status === 'error' && scale.error && !step && <Callout tone="danger">{scale.error}</Callout>}

              <div className="bf-pos-live" data-state={live ? (scale.stable ? 'ok' : 'moving') : 'off'}>
                <span className="bf-pos-live-kg">{live ? formatQty('WEIGHT', Math.max(0, scale.grams ?? 0)) : '—'}</span>
                <span className="bf-pos-live-state">
                  {live
                    ? scale.stable
                      ? 'Peso estable'
                      : 'Pesando…'
                    : scale.status === 'connected'
                      ? 'Conectada, esperando datos…'
                      : 'Sin conectar'}
                </span>
                {scale.raw && <code className="bf-cell-sub">Recibe: {scale.raw}</code>}
              </div>

              <details className="bf-details">
                <summary>Configuración a mano (solo si la detección no funcionó)</summary>
                <div className="bf-form" style={{ marginTop: 8 }}>
                  <p className="bf-cell-sub">Estos datos están en el manual de la balanza, en la parte de “comunicación” o “RS-232”.</p>
                  <div className="bf-form-row">
                    <Field label="Velocidad (baudios)">
                      <Select value={String(c.baudRate)} onChange={(e) => void change({ baudRate: Number(e.target.value) })}>
                        {[1200, 2400, 4800, 9600, 19200, 38400].map((b) => (
                          <option key={b} value={b}>
                            {b}
                          </option>
                        ))}
                      </Select>
                    </Field>
                    <Field label="Formato">
                      <Select
                        value={`${c.dataBits}${c.parity}${c.stopBits}`}
                        onChange={(e) => {
                          const v = e.target.value;
                          void change({
                            dataBits: Number(v[0]) as 7 | 8,
                            parity: v.slice(1, -1) as ScaleLinkConfig['parity'],
                            stopBits: Number(v.slice(-1)) as 1 | 2,
                          });
                        }}
                      >
                        <option value="8none1">8 bits, sin paridad, 1 (8N1)</option>
                        <option value="7even1">7 bits, paridad par, 1 (7E1)</option>
                        <option value="7odd1">7 bits, paridad impar, 1 (7O1)</option>
                        <option value="8even1">8 bits, paridad par, 1 (8E1)</option>
                        <option value="8none2">8 bits, sin paridad, 2 (8N2)</option>
                      </Select>
                    </Field>
                  </div>
                  <div className="bf-form-row">
                    <Field label="Pedido de peso" hint="Algunas balanzas mandan el peso solo cuando se lo piden.">
                      <Select value={c.request} onChange={(e) => void change({ request: e.target.value as ScaleRequest })}>
                        {Object.entries(SCALE_REQUEST_LABELS).map(([k, l]) => (
                          <option key={k} value={k}>
                            {l}
                          </option>
                        ))}
                      </Select>
                    </Field>
                    <Field label="Números sin coma" hint="Ej.: si manda 01250 para 1,250 kg, son gramos.">
                      <Select value={c.unit} onChange={(e) => void change({ unit: e.target.value as ScaleLinkConfig['unit'] })}>
                        <option value="auto">Automático (con coma = kilos, sin coma = gramos)</option>
                        <option value="g">Siempre gramos</option>
                        <option value="kg">Siempre kilos</option>
                      </Select>
                    </Field>
                  </div>
                  <div>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => void change({ ...DEFAULT_SCALE_LINK, enabled: true, usbVendorId: c.usbVendorId, usbProductId: c.usbProductId })}
                    >
                      Volver a los valores comunes
                    </Button>
                  </div>
                </div>
              </details>
            </>
          )}
        </div>
      </CardBody>
    </Card>
  );
}

/* ---------------------------------------------------------------- cajón de dinero */
function DrawerCard({ ticket }: { ticket: TicketConfig }) {
  const hw = useHardware();
  const { me } = useSession();
  return (
    <Card>
      <CardHeader
        title={
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
            <Archive size={18} aria-hidden /> Cajón de dinero
          </span>
        }
        subtitle="Se abre solo al cobrar. Abrirlo sin vender queda registrado."
      />
      <CardBody>
        <div className="bf-form">
          <h4 className="bf-hw-title">Cómo se conecta</h4>
          <Steps>
            <li>
              El cajón <strong>no va a la computadora</strong>: se enchufa a la <strong>impresora de tickets</strong> con el cable tipo teléfono que
              trae (en la impresora el enchufe dice “DK”, “Drawer” o “Cash drawer”).
            </li>
            <li>
              En Windows abrí <strong>Configuración → Impresoras</strong>, elegí la impresora de tickets y entrá a{' '}
              <strong>Preferencias de impresión</strong>.
            </li>
            <li>
              Buscá la opción del cajón (“Cash drawer”, “Cajón” o “Peripheral”) y elegí <strong>abrir antes de imprimir</strong> (o “después”).
              Guardá.
            </li>
            <li>
              Tocá <strong>Probar cajón</strong>: se imprime un papelito y el cajón tiene que abrirse.
            </li>
            <li>
              Prendé la opción de abajo: en la caja aparece el botón <Archive size={14} aria-hidden /> <strong>Abrir cajón</strong>.
            </li>
          </Steps>
          <Callout tone="info">
            Al cobrar una venta, el cajón se abre solo al imprimir el ticket (dejá activado “Imprimir al cobrar” en la pestaña Ticket): dar el vuelto
            de una venta <strong>no</strong> se registra. Solo se registra cuando se abre <strong>sin vender</strong> con el botón (por ejemplo, para
            cambiar billetes): queda en <strong>Control de faltantes → Cajeros</strong> con el motivo. Si se abre con la llave, el sistema no se
            entera: conviene que la llave la tenga solo el dueño o el encargado.
          </Callout>
          <div className="bf-switch-row">
            <div>
              <strong>Hay un cajón conectado a la impresora en esta caja</strong>
              <small>Muestra el botón “Abrir cajón” en la pantalla de venta.</small>
            </div>
            <Switch checked={hw.drawer} onCheckedChange={(v) => setHardware({ ...hw, drawer: v })} label="Cajón conectado" />
          </div>
          <div>
            <Button
              icon={<Printer />}
              onClick={() => printDrawerSlip(ticket.businessName || me.tenant.name, me.user.name, 'Prueba de cajón (no se registra)', ticket)}
            >
              Probar cajón
            </Button>
          </div>
          <p className="bf-cell-sub" style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            <Cable size={14} aria-hidden /> Si el cajón no abre: revisá el cable, que la opción del driver esté guardada y que la impresora sea la
            predeterminada. Algunos cajones traen el cable con otro enchufe: consultá con quien te vendió la impresora.
          </p>
          {hw.drawer && (
            <div>
              <Badge tone="success">Botón “Abrir cajón” activado en esta caja</Badge>
            </div>
          )}
        </div>
      </CardBody>
    </Card>
  );
}
